import { isRecord, requestJson } from "./http.js";
import type { ClientType } from "./sessions.js";

/** A remembered installation: its device key may stand in for the second factor until `rememberUntil`. */
export interface RememberedDevice {
  readonly id: string;
  readonly deviceId: string;
  readonly clientType: ClientType;
  readonly deviceName: string;
  readonly createdAt: number;
  readonly lastUsedAt: number | null;
  /** End of the period in which it replaces the second factor; null when it does not (policy off, expired). */
  readonly rememberUntil: number | null;
  /** The installation of this very app. */
  readonly current: boolean;
}

const CLIENT_TYPES: ReadonlyArray<ClientType> = ["web", "desktop", "extension", "other"];

function clientTypeOf(value: unknown): ClientType {
  return CLIENT_TYPES.find((t: ClientType): boolean => t === value) ?? "other";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function rememberedFrom(value: unknown): RememberedDevice {
  if (!isRecord(value) ||
    typeof value["id"] !== "string" ||
    typeof value["deviceId"] !== "string" ||
    typeof value["clientType"] !== "string" ||
    typeof value["deviceName"] !== "string" ||
    typeof value["createdAt"] !== "number" ||
    (value["lastUsedAt"] !== null && typeof value["lastUsedAt"] !== "number") ||
    (value["rememberUntil"] !== null && typeof value["rememberUntil"] !== "number") ||
    typeof value["current"] !== "boolean") {
    throw unexpected();
  }
  return {
    id: value["id"],
    deviceId: value["deviceId"],
    clientType: clientTypeOf(value["clientType"]),
    deviceName: value["deviceName"],
    createdAt: value["createdAt"],
    lastUsedAt: value["lastUsedAt"],
    rememberUntil: value["rememberUntil"],
    current: value["current"]
  };
}

export function rememberedDevicesFrom(body: unknown): RememberedDevice[] {
  if (!isRecord(body) || !Array.isArray(body["keys"])) {
    throw unexpected();
  }
  return body["keys"].map(rememberedFrom);
}

function okFrom(body: unknown): void {
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
}

export async function listMyRememberedDevices(): Promise<RememberedDevice[]> {
  return rememberedDevicesFrom(await requestJson("/api/v1/device-keys"));
}

/** Forgets the installation: its key is revoked, it signs out and needs the second factor (and enrolment) again. */
export async function forgetMyRememberedDevice(id: string): Promise<void> {
  okFrom(await requestJson(`/api/v1/device-keys/${encodeURIComponent(id)}`, { method: "DELETE" }));
}

export async function listUserRememberedDevices(userId: string): Promise<RememberedDevice[]> {
  return rememberedDevicesFrom(await requestJson(`/api/v1/admin/users/${encodeURIComponent(userId)}/device-keys`));
}

export async function forgetUserRememberedDevice(userId: string, id: string): Promise<void> {
  okFrom(await requestJson(`/api/v1/admin/users/${encodeURIComponent(userId)}/device-keys/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
